import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  api,
  errorMessage,
  ENTITY_STATUSES,
  ENTITY_STATUS_LABELS,
  type AdminLocation,
  type AdminQrCode,
  type AdminRoute,
  type EntityStatus,
} from '@/lib/api';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_authed/qr-codes/')({
  component: QrCodesListPage,
});

const createSchema = z.object({
  code: z
    .string()
    .min(2)
    .max(64)
    .regex(/^[a-z0-9-]+$/u, 'lowercase letters, digits and hyphens only'),
  name: z.string().min(1).max(120),
  targetKind: z.enum(['location', 'route']),
  locationId: z.string().uuid().optional(),
  routeId: z.string().uuid().optional(),
  internalNotes: z.string().optional(),
});

type CreateValues = z.infer<typeof createSchema>;

function QrCodesListPage() {
  const [statusFilter, setStatusFilter] = useState<EntityStatus | 'all'>('all');
  const queryClient = useQueryClient();

  const qrQ = useQuery({
    queryKey: ['qr-codes', statusFilter],
    queryFn: () =>
      api.listQrCodes(statusFilter === 'all' ? {} : { status: statusFilter }),
  });

  // Lookup helpers so the table can show "Monastery of the Holy Cross" rather
  // than a UUID. Cheap to keep all 5 villages → routes → locations in memory
  // at this scale; we'll paginate when the catalogue actually grows.
  const villagesQ = useQuery({ queryKey: ['villages'], queryFn: api.listVillages });
  const allLocations = useAllLocationsAcrossVillages();

  const create = useMutation({
    mutationFn: (body: CreateValues) =>
      api.createQrCode({
        code: body.code,
        name: body.name,
        targetKind: body.targetKind,
        locationId: body.targetKind === 'location' ? (body.locationId ?? null) : null,
        routeId: body.targetKind === 'route' ? (body.routeId ?? null) : null,
        status: 'active',
        internalNotes: body.internalNotes ?? null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qr-codes'] });
    },
  });

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-h1">QR codes</h1>
          <p className="mt-1 text-body text-slate-gray">
            One row per printed sticker. Reassigning a QR doesn’t require reprinting —
            change the target POI here and the old code keeps working.
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-full border border-neutral-gray bg-soft-white p-0.5 text-caption">
          {(['all', ...ENTITY_STATUSES] as const).map((s) => (
            <button
              key={s}
              type="button"
              className={cn(
                'rounded-full px-3 py-1.5 font-semibold transition',
                statusFilter === s
                  ? 'bg-tech-blue text-soft-white'
                  : 'text-slate-gray hover:text-dark-navy',
              )}
              onClick={() => setStatusFilter(s)}
            >
              {s === 'all' ? 'All' : ENTITY_STATUS_LABELS[s]}
            </button>
          ))}
        </div>
      </header>

      <CreateQrForm
        locations={allLocations}
        onSubmit={(v) => create.mutateAsync(v)}
        creating={create.isPending}
        error={create.error ? errorMessage(create.error) : null}
        loadingLocations={villagesQ.isLoading || allLocations === null}
      />

      <section>
        <h2 className="mb-3 text-h2">All QR codes</h2>
        {qrQ.isLoading ? (
          <div className="card text-slate-gray">Loading…</div>
        ) : qrQ.data && qrQ.data.qrCodes.length > 0 ? (
          <div className="overflow-x-auto rounded-card border border-neutral-gray bg-soft-white">
            <table className="w-full text-left text-body">
              <thead className="bg-light-gray text-caption uppercase text-slate-gray">
                <tr>
                  <th className="px-4 py-2">Code</th>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Target</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {qrQ.data.qrCodes.map((q) => (
                  <QrRow
                    key={q.id}
                    q={q}
                    locations={allLocations ?? []}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="card text-slate-gray">
            No QR codes yet. Create one above.
          </div>
        )}
      </section>
    </div>
  );
}

function QrRow({ q, locations }: { q: AdminQrCode; locations: AdminLocation[] }) {
  const linkedLocation = q.locationId ? locations.find((l) => l.id === q.locationId) : null;
  return (
    <tr className="border-t border-neutral-gray/60">
      <td className="px-4 py-2 font-mono text-caption text-dark-navy">{q.code}</td>
      <td className="px-4 py-2 text-dark-navy">{q.name}</td>
      <td className="px-4 py-2 text-slate-gray">
        {q.targetKind === 'location' ? (
          linkedLocation ? (
            <span>
              POI · <span className="font-semibold text-dark-navy">{linkedLocation.title.en}</span>{' '}
              <span className="font-mono text-caption">({linkedLocation.shortId})</span>
            </span>
          ) : (
            <span className="text-warning">POI · unlinked</span>
          )
        ) : (
          <span>Route · {q.routeId?.slice(0, 8)}…</span>
        )}
      </td>
      <td className="px-4 py-2">
        <span
          className={cn(
            'inline-block rounded-full px-2 py-0.5 text-caption font-semibold',
            q.status === 'active' && 'bg-success/15 text-success',
            q.status === 'inactive' && 'bg-warning/15 text-warning',
            q.status === 'archived' && 'bg-light-gray text-slate-gray',
          )}
        >
          {ENTITY_STATUS_LABELS[q.status]}
        </span>
      </td>
      <td className="px-4 py-2 text-right">
        <Link to="/qr-codes/$qrId" params={{ qrId: q.id }} className="btn-ghost">
          Open
        </Link>
      </td>
    </tr>
  );
}

function CreateQrForm({
  locations,
  onSubmit,
  creating,
  error,
  loadingLocations,
}: {
  locations: AdminLocation[] | null;
  onSubmit: (v: CreateValues) => Promise<unknown>;
  creating: boolean;
  error: string | null;
  loadingLocations: boolean;
}) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { targetKind: 'location' },
  });
  const targetKind = watch('targetKind');

  const submit = async (v: CreateValues) => {
    await onSubmit(v);
    reset({ targetKind: 'location' });
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="card space-y-4">
      <h2 className="text-h2">Create QR code</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="label">Code (visitor URL slug)</label>
          <input
            className="input font-mono"
            placeholder="omd-mon-entrance"
            {...register('code')}
          />
          {errors.code ? (
            <p className="mt-1 text-caption text-error">{errors.code.message}</p>
          ) : null}
          <p className="mt-1 text-caption text-slate-gray">
            Visitor lands on <code className="text-dark-navy">explorentia.eu/&lt;code&gt;</code>.
            Stays unique forever — pick once, never reprint.
          </p>
        </div>
        <div>
          <label className="label">Admin name (internal label)</label>
          <input
            className="input"
            placeholder="Monastery entrance — batch Q1 2026"
            {...register('name')}
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="label">Target type</label>
          <select className="input" {...register('targetKind')}>
            <option value="location">POI</option>
            <option value="route" disabled>
              Route landing (coming in M2)
            </option>
          </select>
        </div>
        <div>
          <label className="label">
            {targetKind === 'location' ? 'POI to open' : 'Route landing'}
          </label>
          {loadingLocations ? (
            <div className="input text-slate-gray">Loading POIs…</div>
          ) : (
            <select className="input" {...register('locationId')}>
              <option value="">— Select —</option>
              {(locations ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.title.en} ({l.shortId})
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div>
        <label className="label">Internal notes (optional)</label>
        <input
          className="input"
          placeholder="Printed on matte vinyl, vendor: …"
          {...register('internalNotes')}
        />
      </div>

      {error ? (
        <div className="rounded-lg border border-error/30 bg-error/10 p-3 text-caption text-error">
          {error}
        </div>
      ) : null}

      <button type="submit" className="btn-primary" disabled={creating}>
        {creating ? 'Creating…' : 'Create QR code'}
      </button>
    </form>
  );
}

// Loads all locations across all routes across all villages. With one Omodos
// route this is 5 rows; we'll move to a dedicated `/admin/locations` index
// endpoint once we have multiple villages and the join cost matters.
function useAllLocationsAcrossVillages(): AdminLocation[] | null {
  const villagesQ = useQuery({ queryKey: ['villages'], queryFn: api.listVillages });
  const routesQ = useQuery({
    queryKey: ['all-routes', villagesQ.data?.villages.map((v) => v.id)],
    queryFn: async () => {
      const villages = villagesQ.data?.villages ?? [];
      const allRoutes = await Promise.all(villages.map((v) => api.listRoutes(v.id)));
      return allRoutes.flatMap((r) => r.routes) as AdminRoute[];
    },
    enabled: Boolean(villagesQ.data),
  });
  const locationsQ = useQuery({
    queryKey: ['all-locations', routesQ.data?.map((r) => r.id)],
    queryFn: async () => {
      const allLocs = await Promise.all(
        (routesQ.data ?? []).map((r) => api.listLocations(r.id)),
      );
      return allLocs.flatMap((r) => r.locations);
    },
    enabled: Boolean(routesQ.data && routesQ.data.length > 0),
  });
  return locationsQ.data ?? null;
}
